import React from "react";
//#region types
import { TranslationKeys } from "translations";
import { HTMLConstants, IconDefinitionConstants } from "utils/constants";
import { StyleEnums } from "models/enums";
//#endregion
//#region components
import { DialogActions, Grid } from "@mui/material";
import MenuButton from "../buttons/menuButton";
import RoundButton from "../buttons/roundButton";
import StyledButton from "../buttons/styledButton";
import useFloatingMessages from "../messages/useFloatingMessages";
import useMountedState from "../useMountedState";
import Icon from "../icon";
import SimpleTooltip from "../tooltips/tooltip";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import { DialogFooterProps } from "models/propTypes/dialogPropTypes";

const DialogFooter: React.FC<DialogFooterProps> = (props) => {
  const { closeView, isValid, submitFormContent } = props;
  const [loadingIndex, setLoadingIndex] = React.useState<number | undefined>(undefined);

  const { enqueueMessage } = useFloatingMessages();
  const { getMounted } = useMountedState();

  const close = () => {
    closeView && closeView();
  };

  const onClose = () => {
    props.onClose && props.onClose();
  };

  const actionClick = (
    index: number,
    onClickFunc?: () => Promise<void>,
    successMessage?: string,
    persistOnSubmit?: boolean
  ) => {
    setLoadingIndex(index);

    (onClickFunc
      ? onClickFunc()
      : submitFormContent
      ? submitFormContent()
      : new Promise<void>((resolve) => {
          resolve();
        })
    )
      .then((res) => {
        if (!props.disbleSuccessMessages && successMessage) {
          enqueueMessage(
            LocalizationHelperInstance.getFormattedMessage(successMessage),
            StyleEnums.DisplayType.Success
          );
        }
        props.onSubmit && props.onSubmit(res);
        if (!persistOnSubmit) close();

        getMounted() && setLoadingIndex(undefined);
      })
      .catch((error) => {
        props.onError && props.onError(error, close);
        getMounted() && setLoadingIndex(undefined);
      });
  };

  return (
    <DialogActions className={cssExports.dialogActions}>
      <Grid
        id={HTMLConstants.DialogFooterPortalId}
        item
        container
        xs
        alignItems={"center"}
        className={cssExports.dialogFooter}
      >
        {typeof props.footer === "function" ? props.footer(onClose, isValid) : props.footer}
      </Grid>
      {props.moreActions ? (
        <Grid item>
          <MenuButton
            menuPlacement="top"
            menuItems={
              typeof props.moreActions === "function" ? props.moreActions(onClose, isValid) : props.moreActions
            }
            button={
              <RoundButton className={cssExports.moreActions}>
                <Icon icon={IconDefinitionConstants.WEllipsisH} />
              </RoundButton>
            }
            classes={{
              menuOpen: cssExports.moreActionsMenuOpen,
            }}
            onActionSuccess={onClose}
            disableSuccessMessages={props.disbleSuccessMessages}
          />
        </Grid>
      ) : (
        <></>
      )}
      <Grid item className={cssExports.dialogButtons}>
        {!props.hideCancelButton && (
          <StyledButton
            autoFocus
            onClick={onClose}
            secondary
            size="sm"
            disabled={props.disableCancelWhileSubmitting && loadingIndex !== undefined}
          >
            {props.cancelButtonText ?? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Cancel)}
          </StyledButton>
        )}
        {props.actions ? (
          props.actions.map((action, index) => {
            const isAnotherLoading = loadingIndex !== undefined && loadingIndex !== index;
            const isDisabled =
              ((action.onClick === undefined || action.disableWhenInvalid) && !isValid) ||
              !!action.disabled ||
              isAnotherLoading;
            const button = (
              <StyledButton
                size="sm"
                key={index}
                displayType={action.type}
                secondary={action.isSecondary}
                onClick={actionClick.bind(
                  null,
                  index,
                  action.onClick,
                  action.successMessage,
                  action.persistOnSubmit
                )}
                disabled={isDisabled}
                isSubmitting={loadingIndex === index}
              >
                {action.text}
              </StyledButton>
            );
            // A disabled button gets no pointer events, so the tooltip hangs on a wrapper.
            return isDisabled && action.disabledTooltip ? (
              <SimpleTooltip key={index} text={action.disabledTooltip}>
                <span>{button}</span>
              </SimpleTooltip>
            ) : (
              button
            );
          })
        ) : (
          <></>
        )}
      </Grid>
    </DialogActions>
  );
};

export default DialogFooter;
